// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Divider, Paper, Button, CircularProgress } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PDFDownloadLink } from '@react-pdf/renderer';
import downloadIcon from 'Assets/download-dark-icon.png';
import { useAppDispatch, useAppSelector } from 'Hooks';
import OrderDialog from 'Features/order';
import { RootState } from 'Store';
import { GetOrdersThunk, GetPdfDataThunk } from 'Features/order/orderThunk';
import CustomOrderDialog from 'Features/order/CustomOrderDialog';
import PDF from 'Features/order/PDF';
import ProcessedAcreage from 'Components/ProcessedAcreage';
import Sidebar from 'Components/Sidebar';
import UploadFilesDialog from 'Components/UploadFilesDialog';
import { getOrders } from 'Features/order/orderSelectors';
import { HideUploadSingleFileDialogAction } from 'Features/fileUpload/fileUploadActions';
import { getRasterRequestStatus, selectCRSInfo } from '../projectSelectors';
import Autosuggest from './Autosuggest';
import KmlPanel from './KmlPanel';
import DataSetsPanel from './DataSetsPanel';
import SelectionInfo from './SelectionInfo';
import stylesDef from './styles';

const ProjectSidebar = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { project } = useAppSelector((state) => state.project);
  const { screenshot } = useAppSelector((state) => state.map.common);
  const order = useAppSelector((state: RootState) => getOrders(state).activeOrder);
  const rasterRequest = useSelector((state: RootState) => getRasterRequestStatus(state));

  const { name: projectName, imageryRequest, _id: projectId } = project || {};
  const { rasterizationCreditCost } = order || {};
  const imageryRequested = (order?.isEstimate && !!(project?.imageryRequest?.requested)) || !!(project?.imageryRequest?.uploaded) || !!(project?.imageryRequest?.notificationSent);

  const [orderFormVisible, setOrderFormVisible] = useState(false);
  const [customOrderFormVisible, setCustomOrderFormVisible] = useState(false);
  const [uploadFilesFormVisible, setUploadFilesFormVisible] = useState(false);
  const needUploadFiles = (!order?.boundaryFile);
  const { customCheckoutOptionalRasterization, amtChild } = useFlags();

  const [pdfData, setPdfData] = useState<any>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if ((imageryRequest || !!rasterizationCreditCost) && order.isEstimate) {
      const loadData = async () => {
        await dispatch(GetOrdersThunk(projectId));
      };

      loadData();
    }
  }, [order?.isEstimate, order?.costToCustomer]);

  const onButtonClick = () => {
    if (customCheckoutOptionalRasterization && customCheckoutOptionalRasterization.raster !== undefined) {
      setCustomOrderFormVisible(true);
    } else {
      setOrderFormVisible(true);
    }
  };

  useEffect(() => {
    if (order && order.isEstimate) {
      setPdfLoading(true);
      const fetchPdfData = async () => {
        const result = await dispatch(GetPdfDataThunk(order._id));
        if (result.success) {
          const pdfData = {
            ...result.data.pdfData,
            screenshot,
          }
          setPdfData(pdfData);
        }
        setPdfLoading(false);
      }

      fetchPdfData();
    }

    return () => {
      setPdfData(null);
    };
  }, [order]);

  return (
    <Sidebar onClick={() => dispatch(HideUploadSingleFileDialogAction())}>
      <Box sx={styles.sidebarContent}>
        <Autosuggest />
        <Divider />
        <KmlPanel />
        <Divider />
        <DataSetsPanel />
        <Divider />
        <SelectionInfo />
        <Box sx={styles.acreageWrapper}>
          <ProcessedAcreage />
        </Box>
      </Box>
      {order?.isEstimate && order?.bundleId && (imageryRequested || rasterRequest.requested) && screenshot ? (
        <Box sx={pdfData ? styles.downloadContainer : styles.loadingDownloadContainer}>
          {pdfLoading && <CircularProgress size={24}/>}
          {!pdfLoading && pdfData && (
            <PDFDownloadLink document={<PDF data={pdfData} />} fileName={`${projectName}_estimate.pdf`} style={styles.pdfButton}>
              {({ loading }) =>
                (loading ? <CircularProgress size={24} /> : (
                  <Box
                    sx={styles.orderButton}
                  >
                    <img style={styles.downloadImage} src={downloadIcon} alt="download" />
                    DOWNLOAD ESTIMATE
                  </Box>
                ))}
            </PDFDownloadLink>
          )}
        </Box>
      )
        : (
          <Paper sx={styles.orderButtonWrapper} elevation={2}>
            <span>
              <Button
                variant="outlined"
                fullWidth
                sx={styles.orderButton}
                onClick={onButtonClick}
                disabled={needUploadFiles || order.acreage <= 0 || imageryRequested || rasterRequest.rejected}
              >
                Go to Checkout
              </Button>
            </span>
          </Paper>
        )}
      <OrderDialog orderFormVisible={orderFormVisible} setOrderFormVisible={setOrderFormVisible} />
      <CustomOrderDialog orderFormVisible={customOrderFormVisible} setOrderFormVisible={setCustomOrderFormVisible} />
      <UploadFilesDialog showDialog={uploadFilesFormVisible} setShowDialog={setUploadFilesFormVisible} />
    </Sidebar>
  );
};

export default ProjectSidebar;
